/**
 * Reporte de los errores que **el servidor nunca ve**.
 *
 * ## El punto ciego que cierra
 *
 * La bitácora de la plataforma registra lo que pasa por el backend. Pero un fallo de
 * JavaScript, una promesa rechazada o una petición que no alcanzó a salir **no dejan nada**,
 * por definición: nadie del otro lado recibió algo que anotar. Hasta ahora lo único que
 * quedaba de esos errores era lo que el usuario alcanzara a contar, o una captura de
 * pantalla.
 *
 * ## 🔑 Por qué se guarda en `localStorage` antes de mandarlo
 *
 * Es la decisión central. Mandar el reporte en el momento del error parece más simple y falla
 * justo cuando más importa: **si el error fue una caída de red, el envío también falla**, y
 * el reporte se pierde — precisamente en el escenario que uno quiere investigar. Guardándolo
 * primero, el error sobrevive a la recarga, al cierre de la pestaña y a la caída de red, y se
 * manda en la siguiente oportunidad.
 *
 * O sea: **nada se pierde por no haber podido reportarlo en su momento.**
 *
 * ## Las cuatro reglas que lo mantienen inofensivo
 *
 * Un reportador de errores mal hecho es peor que no tenerlo: puede tumbar la aplicación,
 * llenar el disco del navegador, entrar en bucle o filtrar datos. Las contenciones:
 *
 * 1. **Nunca lanza.** Todo va envuelto en `try/catch`. Si el reportador falla, la aplicación
 *    sigue: el usuario está a media tarea y su trabajo vale más que el registro.
 * 2. **Agrupa repeticiones.** Un error dentro de un ciclo de render se dispara cientos de
 *    veces; se guarda **una** entrada con su contador. Sin esto, un bucle llenaría el
 *    almacenamiento en segundos y el lote inundaría la bitácora.
 * 3. **No se reporta a sí mismo.** Si el envío falla, no se registra ese fallo como un error
 *    nuevo — eso es exactamente el bucle infinito de esta clase de herramienta.
 * 4. **No guarda cuerpos de peticiones ni respuestas.** Solo metadatos: la ruta, el estatus,
 *    el mensaje. La bitácora se lee desde la interfaz, y un cuerpo puede traer datos de un
 *    cliente o una contraseña mal escrita.
 */

import { isChunkLoadFailure, reloadForChunk, shouldReloadForChunk } from './chunkRecovery';

const STORAGE_KEY = 'dylo:pending-errors';

/**
 * Tope de entradas guardadas.
 *
 * Son entradas **distintas** (las repeticiones se agrupan), así que 50 ya describe una
 * sesión muy mala. Al llenarse se descarta lo más viejo: si algo se está rompiendo ahora, los
 * errores recientes explican mejor el estado actual que los de hace media hora.
 */
const MAX_STORED = 50;

/** Tope por envío. Debe coincidir con el `max(20)` del esquema del backend. */
const BATCH_SIZE = 20;

/** Cada cuánto se intenta vaciar la cola, si hay algo pendiente. */
const FLUSH_INTERVAL_MS = 60_000;

interface StoredError {
  message: string;
  kind: 'error' | 'unhandledrejection' | 'network' | 'manual';
  count: number;
  source?: string | null;
  stack?: string | null;
  route?: string | null;
  at?: string | null;
  context?: Record<string, unknown> | null;
  /** Huella para agrupar repeticiones. No se manda. */
  fingerprint: string;
}

/* -------------------------------------------------------------------------- */
/*                            Almacenamiento local                            */
/* -------------------------------------------------------------------------- */

/**
 * Lee la cola.
 *
 * ⚠️ Todo con `try/catch`: `localStorage` **lanza** en modo privado de algunos navegadores y
 * cuando el usuario bloquea el almacenamiento. Un reportador de errores que se cae por no
 * poder guardar un error es un chiste malo.
 */
const read = (): StoredError[] => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const write = (items: StoredError[]): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(-MAX_STORED)));
  } catch {
    /**
     * Cuota agotada o almacenamiento bloqueado. Se intenta limpiar y seguir: perder la cola
     * es mejor que dejar el almacenamiento en un estado que rompa la aplicación en cada
     * carga.
     */
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* nada más que hacer */
    }
  }
};

/**
 * Huella de un error, para agrupar.
 *
 * 🔑 **No incluye la marca de tiempo ni el número de línea completo**, a propósito: el mismo
 * error en el mismo lugar tiene que producir la misma huella para que se cuente en lugar de
 * duplicarse. Se usa tipo + mensaje + ruta, que es lo que identifica "el mismo problema".
 */
const fingerprintOf = (item: Omit<StoredError, 'count' | 'fingerprint'>): string =>
  `${item.kind}|${item.message}|${item.route || ''}`;

/* -------------------------------------------------------------------------- */
/*                                  Registro                                  */
/* -------------------------------------------------------------------------- */

/** Ruta actual de la aplicación. Es el dato que más ubica un problema. */
const currentRoute = (): string => {
  try {
    return `${window.location.pathname}${window.location.hash || ''}`.slice(0, 200);
  } catch {
    return 'unknown';
  }
};

/**
 * Guarda un error en la cola.
 *
 * Si ya hay uno con la misma huella, **incrementa su contador** en lugar de agregar otro. Ver
 * la regla 2 del encabezado.
 */
export const reportError = (input: {
  message: string;
  kind?: StoredError['kind'];
  source?: string | null;
  stack?: string | null;
  context?: Record<string, unknown> | null;
}): void => {
  try {
    const message = String(input.message || '').slice(0, 500).trim();
    if (!message) return;

    const base = {
      message,
      kind: input.kind || 'manual',
      source: input.source ? String(input.source).slice(0, 300) : null,
      stack: input.stack ? String(input.stack).slice(0, 4000) : null,
      route: currentRoute(),
      at: new Date().toISOString(),
      context: input.context || null,
    };

    const fingerprint = fingerprintOf(base);
    const items = read();
    const existing = items.find((i) => i.fingerprint === fingerprint);

    if (existing) {
      existing.count += 1;
      // Se conserva la marca del PRIMER avistamiento: es la que ubica cuándo empezó.
      write(items);
      return;
    }

    items.push({ ...base, count: 1, fingerprint });
    write(items);
  } catch {
    /* regla 1: el reportador nunca puede tumbar la aplicación */
  }
};

/* -------------------------------------------------------------------------- */
/*                                   Envío                                    */
/* -------------------------------------------------------------------------- */

/**
 * Candado de reentrada.
 *
 * Sin esto, dos disparos simultáneos (el temporizador y el arranque, por ejemplo) mandarían
 * el mismo lote dos veces y la bitácora tendría duplicados.
 */
let flushing = false;

/**
 * Manda lo pendiente y limpia lo entregado.
 *
 * ⚠️ **Los errores de este envío NO se reportan** (regla 3). Si fallara y se registrara como
 * un error nuevo, cada intento generaría otro reporte y el navegador se quedaría girando
 * mientras la cola crece. Cuando el envío falla, la cola se conserva y se reintenta después:
 * eso ya es el comportamiento correcto y no necesita anotarse en ningún lado.
 */
export const flushErrors = async (): Promise<void> => {
  if (flushing) return;

  const items = read();
  if (!items.length) return;

  flushing = true;
  try {
    const batch = items.slice(0, BATCH_SIZE);

    /**
     * 🔑 **Se usa `fetch` y NO la instancia de axios del proyecto, por dos razones.**
     *
     * 1. **Rompe una importación circular.** El interceptor de axios llama a `reportError`
     *    para capturar las peticiones que no obtienen respuesta; si este archivo importara
     *    esa instancia, cada uno dependería del otro. Con `fetch` no hay ciclo.
     * 2. **Evita la reentrada.** Ese interceptor renueva la sesión ante un 401 y reintenta la
     *    petición. Un reporte de diagnóstico no debe disparar una renovación de sesión ni
     *    reintentos automáticos: si falla, se queda en la cola y se manda después.
     *
     * `credentials: 'include'` es obligatorio: el endpoint exige sesión y la cookie es
     * HttpOnly de otro dominio.
     */
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3306/api';
    const response = await fetch(`${baseUrl}/client-errors`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        // `fingerprint` es interno del cliente y no viaja: al backend le sirve el `kind`.
        errors: batch.map(({ fingerprint, ...rest }) => rest),
        appVersion: process.env.REACT_APP_VERSION || 'dev',
        /**
         * 📌 Se manda el user agent del cliente aunque el backend ya tenga el del encabezado.
         * No son lo mismo: el error pudo ocurrir en otra pestaña o en otra sesión y guardarse
         * para después, así que el del encabezado sería el del **envío** y no el del fallo.
         */
        userAgent: navigator.userAgent,
      }),
    });

    /**
     * ⚠️ Un 4xx **no** se reintenta: el lote se descarta.
     *
     * Si el backend lo rechaza por forma o por falta de sesión, reintentarlo daría el mismo
     * resultado para siempre y ese lote quedaría atorado bloqueando a los siguientes. Un
     * reporte de diagnóstico perdido es aceptable; una cola atascada no.
     *
     * Un 5xx o una caída de red sí conservan la cola, porque son transitorios: ahí el
     * `throw` de abajo la deja intacta.
     */
    if (response.status >= 500) throw new Error(`server ${response.status}`);

    /**
     * Se relee antes de recortar, no se usa `items`.
     *
     * Entre el envío y este punto pudieron llegar errores nuevos, y sobrescribir con la lista
     * vieja los borraría — perdiendo justo los que ocurrieron mientras se reportaba, que
     * suelen ser los interesantes. Se quitan solo los entregados, por huella.
     */
    const delivered = new Set(batch.map((b) => b.fingerprint));
    write(read().filter((i) => !delivered.has(i.fingerprint)));
  } catch {
    /* regla 3: no se reporta el fallo del reporte. La cola se queda y se reintenta. */
  } finally {
    flushing = false;
  }
};

/* -------------------------------------------------------------------------- */
/*                                 Instalación                                */
/* -------------------------------------------------------------------------- */

let installed = false;

/**
 * Engancha los dos eventos globales del navegador y arranca el envío diferido.
 *
 * ## Cuándo se vacía la cola
 *
 * - **Al arrancar**, con un retraso: es lo que recupera los errores de la sesión anterior,
 *   incluidos los que tumbaron la pantalla. El retraso evita competir con las peticiones de
 *   la carga inicial, que son las que el usuario está esperando.
 * - **Cada minuto**, si hay algo pendiente.
 * - **Al ocultarse la pestaña**, que es lo más cerca que se puede estar de "antes de cerrar"
 *   sin usar `beforeunload`.
 *
 * 📌 No se usa `sendBeacon`: nuestra API vive en otro dominio y el envío necesita la cookie
 * de sesión. Y no hace falta — la cola persiste en `localStorage`, así que lo que no alcance
 * a salir se manda en el siguiente arranque. Es la ventaja de haber guardado primero.
 */
export const installErrorReporter = (): void => {
  // Idempotente: en desarrollo, React monta dos veces con StrictMode y sin esto quedarían
  // dos temporizadores y dos escuchas, o sea reportes duplicados.
  if (installed) return;
  installed = true;

  try {
    window.addEventListener('error', (event) => {
      const source = event.filename
        ? `${event.filename}:${event.lineno}:${event.colno}`
        : null;

      /**
       * 🔴 **Aquí es donde aterriza un chunk que no carga**, y por eso la recuperación va en este manejador y no
       * solo en el `ErrorBoundary`.
       *
       * El error que se vio —`this.errorCb is not a function` desde el chunk de CSS de Mapbox— se lanza dentro
       * del `onload` de una etiqueta `<link>` que crea webpack. Eso es una devolución de llamada del navegador,
       * **fuera del árbol de React**: `componentDidCatch` no lo ve nunca. Poner solo el boundary habría dejado el
       * error exactamente igual de visible.
       *
       * 📌 `recoverFromChunkFailure` ya reporta por su cuenta, así que aquí se corta para no reportar dos veces
       * el mismo hecho con dos textos distintos.
       */
      if (isChunkLoadFailure({ message: event.message, name: event.error?.name })) {
        const reloading = shouldReloadForChunk();

        /** 📌 Se reporta **antes** de saltar: la recarga descarta el proceso y un reporte posterior no saldría. */
        reportError({
          kind: 'error',
          message: reloading
            ? `Chunk load failed, reloading once: ${event.message}`
            : `Chunk load failed again after reload: ${event.message}`,
          source: source || 'window.error',
          stack: event.error?.stack || null,
          context: { where: 'window.error', reloaded: reloading },
        });

        if (reloading) reloadForChunk();
        return;
      }

      reportError({
        kind: 'error',
        message: event.message || 'Unknown script error',
        source,
        stack: event.error?.stack || null,
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      const reason: any = event.reason;
      const message =
        reason?.message ||
        (typeof reason === 'string' ? reason : null) ||
        'Unhandled promise rejection';

      /**
       * ⚠️ Un `import()` que falla llega **por aquí**, como promesa rechazada, no como error de script. Son dos
       * caminos para el mismo fallo y hay que cubrir los dos: `React.lazy` lo convierte en error de render —que sí
       * ve el boundary— pero solo si el componente se llega a renderizar. Si se navegó fuera antes, la promesa se
       * rechaza sin que nadie la espere y termina aquí.
       */
      if (isChunkLoadFailure({ message, name: reason?.name })) {
        const reloading = shouldReloadForChunk();

        reportError({
          kind: 'error',
          message: reloading
            ? `Chunk load failed, reloading once: ${message}`
            : `Chunk load failed again after reload: ${message}`,
          source: 'unhandledrejection',
          stack: reason?.stack || null,
          context: { where: 'unhandledrejection', reloaded: reloading },
        });

        if (reloading) reloadForChunk();
        return;
      }

      reportError({
        kind: 'unhandledrejection',
        /**
         * `reason` puede ser cualquier cosa: un `Error`, un string, un objeto de axios. Se
         * intenta sacar algo legible en ese orden de preferencia.
         */
        message,
        stack: reason?.stack || null,
      });
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') void flushErrors();
    });

    // Retraso al arrancar: primero lo que el usuario espera, después el diagnóstico.
    window.setTimeout(() => void flushErrors(), 4000);
    window.setInterval(() => void flushErrors(), FLUSH_INTERVAL_MS);
  } catch {
    /* regla 1 */
  }
};
